// React hook orchestrating the WASM sim, the tick loop, and the WS bridge to
// game-dom-ws-server. Returns reactive state for the UI plus actions to fire
// intents and control time (pause / play / setSpeed) back into the sim.
// The intent actions live in ./useSimIntents.ts; time controls stay here.

import type { TickLoop, createRustSimAdapter } from '@aod/sim-adapter';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { GameDomWsBridge } from '../wsBridge.js';
import { applyFrame as applyFrameToAdapter } from './applyIntent.js';
import { bootSim } from './bootSim.js';
import { PLACEHOLDER_SIM_WORLD, type SimWorld, type UseSimWorldOptions } from './types.js';
import { useDynastyIntents } from './useDynastyIntents.js';
import { useEspionageIntents } from './useEspionageIntents.js';
import { useHeroCaptivityIntents } from './useHeroCaptivityIntents.js';
import { useHeroQuestIntents } from './useHeroQuestIntents.js';
import { useSimIntents } from './useSimIntents.js';
import { useTreatyIntents } from './useTreatyIntents.js';

export function useSimWorld(options: UseSimWorldOptions = {}): SimWorld {
  const [state, setState] = useState<SimWorld>(PLACEHOLDER_SIM_WORLD);
  const adapterRef = useRef<ReturnType<typeof createRustSimAdapter> | null>(null);
  const wsRef = useRef<GameDomWsBridge | null>(null);
  const loopRef = useRef<TickLoop | null>(null);
  const publishRef = useRef<(() => void) | null>(null);
  const localPlayerIndex = options.localPlayerIndex ?? 0;
  const eventsRef = useRef<Array<{ id: number; type: string; payload?: Record<string, unknown> }>>(
    [],
  );
  const unsubRef = useRef<(() => void) | null>(null);

  const dynasty = useDynastyIntents(adapterRef, localPlayerIndex);
  const espionage = useEspionageIntents(adapterRef, localPlayerIndex);
  const heroCaptivity = useHeroCaptivityIntents(adapterRef, localPlayerIndex);
  const heroQuests = useHeroQuestIntents(adapterRef, localPlayerIndex);
  const treaty = useTreatyIntents(adapterRef, localPlayerIndex);
  const {
    invest,
    attackCity,
    moveUnit,
    sailShip,
    dockTrade,
    acceptMerchant,
    declineMerchant,
    buildBridge,
    startBuilding,
    setCityDoctrine,
    summonLegend,
    activateLegendAbility,
    suggestIntents,
    deployUnit,
    setFormation,
    startResearch,
    pilgrimage,
    reform,
    activateHeroAbility,
    selectHeroAbility,
  } = useSimIntents({ adapterRef, localPlayerIndex, publishRef });

  // ---- Time controls. Each mutates the loop and force-publishes so the UI
  //      reflects the new state (run state / speed) immediately, even when
  //      paused (where no onTick will fire to do it for us).
  const pause = useCallback(() => {
    const loop = loopRef.current;
    if (!loop) return;
    loop.pause();
    setState((prev) =>
      prev.status === 'ready' ? { ...prev, runState: loop.getRunState() } : prev,
    );
  }, []);

  const resume = useCallback(() => {
    const loop = loopRef.current;
    if (!loop) return;
    loop.resume();
    setState((prev) =>
      prev.status === 'ready' ? { ...prev, runState: loop.getRunState() } : prev,
    );
  }, []);

  const setSpeed = useCallback((multiplier: number) => {
    const loop = loopRef.current;
    if (!loop) return;
    loop.setSpeed(multiplier);
    setState((prev) => (prev.status === 'ready' ? { ...prev, speed: loop.getSpeed() } : prev));
  }, []);

  // Dev/test only (reached through the test bridge): step the WASM sim in
  // batches and publish once, so a full match can be played in seconds
  // instead of one React publish per tick. The sim freezes itself once a
  // winner is set; the publish then auto-pauses the loop via the usual
  // victory path.
  const fastForward = useCallback((ticks: number) => {
    const adapter = adapterRef.current;
    const publish = publishRef.current;
    if (!adapter || !publish) return;
    for (let left = Math.max(0, Math.floor(ticks)); left > 0; ) {
      const n = Math.min(left, 250);
      adapter.step(n);
      left -= n;
    }
    publish();
  }, []);

  const newMatch = useCallback((newSeed?: number) => {
    const adapter = adapterRef.current;
    const loop = loopRef.current;
    const publish = publishRef.current;
    if (!adapter || !loop) return;
    const seed = newSeed ?? Math.floor(Math.random() * 0xffff_ffff);
    adapter.reset(seed);
    eventsRef.current = [];
    if (loop.getRunState() !== 'running') {
      loop.stop();
      loop.start();
    }
    publish?.();
  }, []);

  const saveGame = useCallback(() => {
    const adapter = adapterRef.current as Record<string, unknown> | null;
    if (!adapter) return '';
    return (adapter.saveGame as () => string)();
  }, []);

  const loadGame = useCallback((json: string) => {
    const adapter = adapterRef.current as Record<string, unknown> | null;
    if (!adapter) return;
    (adapter.loadGame as (json: string) => void)(json);
    // Show the loaded state now, even while paused (the tick loop only
    // republishes on the next tick).
    publishRef.current?.();
  }, []);

  const applyFrame = useCallback((frame: Record<string, readonly unknown[]>) => {
    const adapter = adapterRef.current;
    if (!adapter) return;
    applyFrameToAdapter(
      adapter as unknown as { intent: (intent: Record<string, unknown>) => unknown },
      frame,
    );
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fastForward is stable (useCallback with [] deps); dep list kept as-is
  useEffect(() => {
    let cancelled = false;
    let cleanupFn: (() => void) | undefined;

    const refs = {
      adapter: adapterRef,
      ws: wsRef,
      loop: loopRef,
      publish: publishRef,
      events: eventsRef,
      unsub: unsubRef,
    };

    bootSim(
      {
        configJson: options.configJson,
        ruleset: options.ruleset,
        mapJson: options.mapJson,
        seats: options.seats,
        teams: options.teams,
        matchSeed: options.matchSeed ?? 42,
        tickRateHz: options.tickRateHz ?? 10,
        localPlayerIndex,
        externalTick: options.externalTick ?? false,
        invest,
        attackCity,
        moveUnit,
        sailShip,
        dockTrade,
        acceptMerchant,
        declineMerchant,
        dynasty,
        espionage,
        heroCaptivity,
        heroQuests,
        treaty,
        buildBridge,
        deployUnit,
        setFormation,
        startResearch,
        pilgrimage,
        reform,
        startBuilding,
        setCityDoctrine,
        suggestIntents,
        summonLegend,
        activateLegendAbility,
        activateHeroAbility,
        selectHeroAbility,
        pause,
        resume,
        setSpeed,
        fastForward,
        newMatch,
        saveGame,
        loadGame,
        setState,
      },
      refs,
    ).then((result) => {
      if (cancelled) {
        result.cleanup();
      } else {
        cleanupFn = result.cleanup;
        const simExternalStep = result.externalStep;
        setState((prev) => ({
          ...prev,
          externalStep: simExternalStep ?? (() => {}),
          applyFrame,
          getDeterminismHash: () => adapterRef.current?.determinismHash() ?? 0n,
        }));
      }
    });

    return () => {
      cancelled = true;
      cleanupFn?.();
    };
  }, [
    options.matchSeed,
    options.tickRateHz,
    options.configJson,
    options.ruleset,
    options.seats,
    options.teams,
    options.mapJson,
    options.externalTick,
    localPlayerIndex,
    applyFrame,
    invest,
    pause,
    resume,
    setSpeed,
    attackCity,
    newMatch,
    moveUnit,
    sailShip,
    dockTrade,
    acceptMerchant,
    declineMerchant,
    dynasty,
    espionage,
    heroCaptivity,
    heroQuests,
    treaty,
    buildBridge,
    deployUnit,
    setFormation,
    startResearch,
    pilgrimage,
    reform,
    startBuilding,
    setCityDoctrine,
    suggestIntents,
    summonLegend,
    activateLegendAbility,
    activateHeroAbility,
    selectHeroAbility,
    saveGame,
    loadGame,
  ]);

  return state;
}
